import React, { useState, useEffect } from 'react';
import { useQuery } from 'hooks/useQuery';

import HeaderMagraGroup from 'img/header-mafra-group.svg';
import HeaderLogoCCare from 'img/customer-care-header-logo.svg';

import { Row } from 'components/Layout/Grid';
import { Visibility } from 'components/Visibility';
import { Button } from 'components/Button';
import Text from 'components/Text';
import { PageSuccessByEmail } from 'components/RequestReturnPageByEmail/PageSuccessByEmail';
import { PageWarningByEmail } from 'components/RequestReturnPageByEmail/PageWarningByEmail';
import Loading from 'components/Loading';

import { ITickets } from 'models/Ticket';

import { EnumErrorCodeRequest, ErrorState } from './interface';

import TicketServices from 'services/ticket.service';
import { formatDocument } from 'utils/Document';

import { ModalRecuse } from './ModalRecuse';
import { OrderItems } from './OrderItems';
import { TicketDetails } from './TicketDetails';

import * as Styled from './styles';

interface ErrorMessageProps {
  message: string;
}

const ErrorMessage: React.FC<ErrorMessageProps> = ({ message }) => (
  <PageWarningByEmail title={message} />
);

const LoadingIndicator: React.FC = () => (
  <Row className="align-center justify-center mt-10">
    <Loading onlySpinner size={50} />
  </Row>
);

export const ReturnProcessor: React.FC = () => {
  const [errorRequest, setErrorRequest] = useState<ErrorState>({
    isError: false,
    typeError: "",
    message: "",
    isErrorField: false
  });

  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [approved, setApproved] = useState<boolean>(false);
  const [internalComment, setInternalComment] = useState<string>("");

  const [isLoadingPage, setIsLoadingPage] = useState<boolean>(false);
  const [isOpenModalRecuse, setIsOpenModalRecuse] = useState<boolean>(false);

  const [ticket, setTicket] = useState<ITickets | undefined>(undefined);
  const [title, setTitle] = useState<string>("");

  const ticketIdParam: string | null = useQuery().get('ticketId') ?? "";
  const phaseParam: string | null = useQuery().get('phase') ?? "";

  const _ticketService = new TicketServices();

  useEffect(() => {
    getTicket();
  }, []);

  const getTicket = async () => {
    setIsLoadingPage(true);

    if (!ticketIdParam) {
      addErrorRequest(true, EnumErrorCodeRequest.PROTOCOL_NOT_FOUND, "Protocolo não encontrado em nosso sistema.");
      setIsLoadingPage(false);
      return false;
    }

    const result = await _ticketService.getDevolutionTicketApproval(ticketIdParam, phaseParam);

    if (result.success) {
      const data = result.data;
      if (data.isAbleToValidate === false) {
        addErrorRequest(true, EnumErrorCodeRequest.INVALID_VALIDATION, data.message);
      } else {
        const documentNumber = data.ticket.cnpj ?? "00000000000000";
        data.ticket.cnpj = formatDocument(documentNumber);

        setTicket(data.ticket);
        setTitle(data.title);
      }
    } else {
      addErrorRequest(true, EnumErrorCodeRequest.PROTOCOL_NOT_FOUND, result.message);
    }

    setIsLoadingPage(false);
  };

  const sendValidProcess = async (isApproved: boolean) => {
    if (isApproved === false && internalComment.trim() === "") {
      addErrorRequest(false, EnumErrorCodeRequest.FIELD_INVALID, "Campo obrigatório", true);
      return;
    }

    setApproved(isApproved);
    setIsLoadingPage(true);
    setIsOpenModalRecuse(false);

    const dataObj = {
      ticketId: ticketIdParam,
      isApproved: isApproved,
      internalComment: isApproved ? "" : internalComment,
    };

    try {
      const result = await _ticketService.saveDevolutionTicketApprovalData(dataObj);
      if (result.success) {
        setIsSuccess(true);
      } else {
        const errorMessage = result.message ?? "Erro desconhecido";
        addErrorRequest(true, EnumErrorCodeRequest.GENERIC_ERROR, errorMessage);
        setIsSuccess(false);
      }
    } catch (error) {
      setIsSuccess(false);
      addErrorRequest(true, EnumErrorCodeRequest.GENERIC_ERROR, "Ocorreu um erro inesperado na requisição");
    } finally {
      setIsLoadingPage(false);
    }
  };



  const openCloseModalRecuse = () => {
    setIsOpenModalRecuse(!isOpenModalRecuse);
  };

  const addErrorRequest = (isError: boolean, field: string, msg: string, isErrorField: boolean = false) => {
    setErrorRequest({
      isError: isError,
      typeError: field,
      message: msg || "Ocorreu um erro.",
      isErrorField: isErrorField
    });

    return true;
  };

  const handleInternalComment = (value: string) => {
    addErrorRequest(false, EnumErrorCodeRequest.FIELD_INVALID, "", false);
    setInternalComment(value);
  };

  const conditionShowScheduleSuccess = isSuccess && !errorRequest.isError && !errorRequest.isErrorField;
  const conditionShowPage = !isSuccess && !errorRequest.isError && !isLoadingPage;
  const isError = errorRequest.isError && !errorRequest.isErrorField;

  return (
    <>
      <ModalRecuse
        isOpen={isOpenModalRecuse}
        onClose={openCloseModalRecuse}
        onSubmit={sendValidProcess}
        internalComment={internalComment}
        handleInternalComment={handleInternalComment}
        errorRequest={errorRequest}
      />

      <Styled.Container>
        <div className="header-logo-box mr-2">
          <img className="mr-3" src={HeaderMagraGroup} alt="" />
          <img src={HeaderLogoCCare} alt="" width={110} height={30} />
        </div>

        <Styled.Content>
          <Visibility visible={isLoadingPage}>
            <LoadingIndicator />
          </Visibility>

          <Visibility visible={isError}>
            <ErrorMessage message={errorRequest.message} />
          </Visibility>

          <Visibility visible={conditionShowScheduleSuccess}>
            <PageSuccessByEmail title={approved ? 'Processo validado' : 'Processo recusado'} />
          </Visibility>

          <Visibility visible={conditionShowPage}>
            <Text className="color-primary font-bold" size={24}>
              {title} | Avaliar processo de devolução
            </Text>

            <TicketDetails ticket={ticket} />

            <OrderItems items={ticket?.order?.items || []} />

            <Styled.FooterReturnProcessor className='align-center'>
              <Styled.ButtonRefuse width={160} onClick={openCloseModalRecuse}>
                <Text className="color-primary font-bold px-1">Recusar Processo</Text>
              </Styled.ButtonRefuse>
              <Button className="outline" width={160} onClick={() => sendValidProcess(true)}>
                <Text className="color-primary font-bold px-1">Validar Processo</Text>
              </Button>
            </Styled.FooterReturnProcessor>
          </Visibility>
        </Styled.Content>
      </Styled.Container>
    </>
  );
};
